import React, { useRef } from 'react';
import { InputGroup } from 'react-bootstrap';
import Flatpickr, { DateTimePickerProps } from 'react-flatpickr';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FlairIcon from '../../atomic/atoms/FlairIcon';
import { useCurrentLocale } from '../../context/I18n';
import { toFlatpickrLocale } from '../form/flatpickrUtils';
import { FormGroup } from '../form/FormGroup';
import InputError from '../form/InputError';
import InputLabel from '../form/InputLabel';
import ScrollToError from './ScrollToError';
import { getError } from './utils';

type Props = Pick<DateTimePickerProps, 'options'> & {
  type?: 'date' | 'datetime';
  label: string;
  name: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  required?: boolean;
};

type ControlledDateInputProps = Omit<Props, 'label' | 'required'>;

export const ControlledDateInput: React.FC<
  React.PropsWithChildren<ControlledDateInputProps>
> = ({ type = 'date', name, options, disabled, children, ...rest }) => {
  const locale = toFlatpickrLocale(useCurrentLocale());
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = getError(name, errors);

  const defaultOptions = {
    locale,
    dateFormat:
      type === 'datetime'
        ? t('flatpickr.datetimeFormat')
        : t('flatpickr.dateFormat'),
    enableTime: type === 'datetime',
  };

  return (
    <InputGroup className="input-group-merge">
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Flatpickr
            {...field}
            value={field.value ?? ''}
            className={`form-control form-control-appended ${
              !disabled && error ? 'is-invalid' : ''
            }`}
            options={{
              ...defaultOptions,
              ...options,
              ...(disabled ? { clickOpens: false } : {}),
            }}
            disabled={disabled}
            {...rest}
            onChange={(dates) => field.onChange(dates[0] ?? null)}
          />
        )}
      />
      <InputGroup.Text className={disabled ? 'disabled' : ''}>
        <FlairIcon icon="calendar-clear-outline" />
      </InputGroup.Text>
      <InputError error={error} />
    </InputGroup>
  );
};

type PlainDateInputProps = {
  type?: 'date' | 'datetime';
  value: Date | null;
  onChange: (date: Date | null) => void;
  onBlur?: () => void;
  error?: boolean;
  disabled?: boolean;
  options?: DateTimePickerProps['options'];
};

export const PlainDateInput: React.FC<
  React.PropsWithChildren<PlainDateInputProps>
> = ({ type = 'date', value, onChange, onBlur, error, options, disabled }) => {
  const locale = toFlatpickrLocale(useCurrentLocale());
  const { t } = useTranslation();

  const defaultOptions = {
    locale,
    dateFormat:
      type === 'datetime'
        ? t('flatpickr.datetimeFormat')
        : t('flatpickr.dateFormat'),
    enableTime: type === 'datetime',
  };

  return (
    <InputGroup className="input-group-merge">
      <Flatpickr
        value={value ?? undefined}
        className={`form-control form-control-appended ${
          error ? 'is-invalid' : ''
        }`}
        options={{
          ...defaultOptions,
          ...options,
          ...(disabled ? { clickOpens: false } : {}),
        }}
        onChange={(dates) => onChange(dates[0] ?? null)}
        onClose={() => onBlur?.()}
        disabled={disabled}
      />
      <InputGroup.Text className={disabled ? 'disabled' : ''}>
        <FlairIcon icon="calendar-clear-outline" />
      </InputGroup.Text>
    </InputGroup>
  );
};

export const Datepicker: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  label,
  required,
  ...rest
}) => {
  const ref = useRef(null);

  return React.useMemo(
    () => (
      <FormGroup ref={ref}>
        <InputLabel label={label} required={required} />
        <ControlledDateInput name={name} {...rest} />
        <ScrollToError name={name} inputContainerRef={ref} />
      </FormGroup>
    ),
    [name, label, required, ref, rest],
  );
};
